import { getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { IPaginatedResponse, ISupplierCatalogProduct } from 'pages/supplier-intelligence/utils/types';
import type { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/supplier-intelligence/suppliers';

type TData = IPaginatedResponse<ISupplierCatalogProduct>;
type TError = AxiosError;
type TQueryKey = [
  string,
  { id: number; page: TParam; limit: TParam; search: TParam; sort: TParam; category: TParam; brand: TParam; unit: TParam },
];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetSiSupplierCatalogApi(id: number, options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = 10;
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { search, sort, category, brand, unit } = getPageParams();

  const queryFn = async () => {
    try {
      const params: any = { page, limit };
      if (search) params.search = search;
      if (sort) params.sort = sort;
      if (category) params.category = category;
      if (brand) params.brand = brand;
      if (unit) params.unit = unit;
      const { data } = await http.get(`${endpoint}/${id}/catalog`, { params });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useQueryWrapper(
    [`${endpoint}/${id}/catalog`, { id, page, limit, search, sort: sort ?? null, category: category ?? null, brand: brand ?? null, unit: unit ?? null }],
    queryFn,
    {
      enabled: isLoggedIn && !!id,
      ...options,
    }
  );
}
